import { useRef, useState } from 'react'

import { leerRut, nombreYApellidos, type DatosDelRut } from '@/lib/rut/leerRut'

import estilos from './CargarRut.module.css'

type Props = {
  /** Lo leído, ya listo para llenar el formulario. */
  onLeido: (datos: {
    identificacion: string
    nombre: string
    apellidos: string
    esEmpresa: boolean
    direccion: string
    ciudad: string
    telefono: string
    correo: string
  }) => void
}

/**
 * Crear el cliente desde su RUT.
 *
 * El RUT de la DIAN es un PDF con texto, así que se lee ENTERO EN EL
 * NAVEGADOR: el documento tributario del cliente no sale del computador de
 * quien lo carga, ni siquiera llega a Supabase.
 *
 * Lo leído no se manda solo: se muestra para que alguien lo confirme. Un PDF
 * mal leído que entra derecho a la contabilidad es peor que teclear a mano.
 */
export function CargarRut({ onLeido }: Props) {
  const entrada = useRef<HTMLInputElement>(null)
  const [leyendo, setLeyendo] = useState(false)
  const [datos, setDatos] = useState<DatosDelRut | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [encima, setEncima] = useState(false)

  async function procesar(archivo: File) {
    setError(null)
    setDatos(null)

    if (!archivo.name.toLowerCase().endsWith('.pdf')) {
      setError('El RUT tiene que ser el PDF que expide la DIAN.')
      return
    }

    setLeyendo(true)

    try {
      const leido = await leerRut(archivo)
      setDatos(leido)

      // Se llena el formulario de una vez con lo que sí se pudo leer, para
      // que quien carga solo tenga que revisar y completar.
      const { nombre, apellidos } = nombreYApellidos(leido)

      onLeido({
        identificacion: leido.identificacion ?? '',
        nombre,
        apellidos,
        esEmpresa: leido.esEmpresa,
        direccion: leido.direccion ?? '',
        ciudad: leido.ciudad ?? '',
        telefono: leido.telefono ?? '',
        correo: leido.correo ?? '',
      })
    } catch {
      setError(
        'No se pudo leer ese PDF. Si es un RUT escaneado —una foto dentro del PDF— no tiene texto que leer: toca digitarlo.',
      )
    } finally {
      setLeyendo(false)
    }
  }

  return (
    <div className={estilos.zona}>
      <div
        className={`${estilos.caja} ${encima ? estilos.encima : ''} ${datos ? estilos.leido : ''}`}
        onDragOver={(e) => {
          e.preventDefault()
          setEncima(true)
        }}
        onDragLeave={() => setEncima(false)}
        onDrop={(e) => {
          e.preventDefault()
          setEncima(false)
          const archivo = e.dataTransfer.files[0]
          if (archivo) void procesar(archivo)
        }}
        onClick={() => entrada.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') entrada.current?.click()
        }}
      >
        <input
          ref={entrada}
          type="file"
          accept="application/pdf,.pdf"
          className={estilos.oculto}
          onChange={(e) => {
            const archivo = e.target.files?.[0]
            if (archivo) void procesar(archivo)
          }}
        />

        {leyendo ? (
          <p className={estilos.texto}>Leyendo el RUT…</p>
        ) : datos ? (
          <p className={estilos.texto}>
            <b>RUT leído.</b> Revise abajo antes de crear.
          </p>
        ) : (
          <>
            <p className={estilos.texto}>
              <b>Cargue el RUT</b> y los datos se llenan solos
            </p>
            <p className={estilos.pie}>
              El PDF de la DIAN. Se lee aquí mismo: no sale de este computador.
            </p>
          </>
        )}
      </div>

      {error && <p className={estilos.error}>{error}</p>}

      {/* Lo que se leyó, para poder desconfiar de ello. */}
      {datos && (
        <div className={estilos.resumen}>
          <Dato rotulo={datos.esEmpresa ? 'NIT' : 'Identificación'} valor={datos.identificacion} />
          <Dato
            rotulo={datos.esEmpresa ? 'Razón social' : 'Nombre'}
            valor={
              datos.esEmpresa
                ? datos.razonSocial
                : [
                    datos.primerNombre,
                    datos.otrosNombres,
                    datos.primerApellido,
                    datos.segundoApellido,
                  ]
                    .filter(Boolean)
                    .join(' ') || null
            }
          />
          <Dato rotulo="Dirección" valor={datos.direccion} />
          <Dato rotulo="Ciudad" valor={datos.ciudad} />
          <Dato rotulo="Teléfono" valor={datos.telefono} />
          <Dato rotulo="Correo" valor={datos.correo} />

          {datos.faltantes.length > 0 && (
            <p className={estilos.faltantes}>
              No se pudo leer {datos.faltantes.join(' ni ')}. Complételo abajo.
            </p>
          )}

          <p className={estilos.nota}>
            La dirección, la ciudad, el teléfono y el correo quedan abajo para revisarlos antes de
            crear el cliente.
          </p>
        </div>
      )}
    </div>
  )
}

function Dato({ rotulo, valor }: { rotulo: string; valor: string | null }) {
  return (
    <div className={estilos.dato}>
      <span className={estilos.datoRotulo}>{rotulo}</span>
      <span className={`${estilos.datoValor} ${valor ? '' : estilos.datoVacio}`}>
        {valor ?? 'no vino'}
      </span>
    </div>
  )
}
